<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleManagementQueueRow from './PostSaleManagementQueueRow.vue';

const props = defineProps({
  loading: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
  savingUserId: { type: [Number, String], default: null },
  updatedAt: { type: Date, default: null },
});

const emit = defineEmits(['refresh', 'toggleQueue']);
const { t } = useI18n();
const clock = ref(Date.now());
let timer;

const total = computed(() =>
  props.rows.reduce((sum, row) => sum + (row.total || 0), 0)
);
const lastUpdated = computed(() => {
  if (!props.updatedAt) return '';
  return Math.max(
    0,
    Math.floor((clock.value - props.updatedAt.getTime()) / 60000)
  );
});

onMounted(() => {
  timer = window.setInterval(() => {
    clock.value = Date.now();
  }, 60000);
});
onBeforeUnmount(() => window.clearInterval(timer));
</script>

<template>
  <LaisCard :title="t('POST_SALE.MANAGEMENT.QUEUE')" density="flush">
    <template #titleAction>
      <LaisPrimaryButton
        icon="i-lucide-refresh-cw"
        variant="ghost"
        :is-loading="loading"
        @click="emit('refresh')"
      >
        {{ t('POST_SALE.MANAGEMENT.QUEUE_REFRESH') }}
      </LaisPrimaryButton>
    </template>

    <div
      class="flex items-center justify-between gap-3 border-b border-lais-border-soft px-5 py-3 text-sm"
    >
      <span class="font-medium text-lais-text">
        {{ t('POST_SALE.MANAGEMENT.QUEUE_TOTAL', { count: total }) }}
      </span>
      <span v-if="lastUpdated" class="text-lais-text-muted">
        {{
          t('POST_SALE.MANAGEMENT.QUEUE_UPDATED_AT', { minutes: lastUpdated })
        }}
      </span>
    </div>

    <div class="overflow-x-auto">
      <table
        class="min-w-[980px] w-full divide-y divide-lais-border-soft text-sm"
      >
        <thead
          class="bg-lais-bg-soft text-xs font-semibold text-lais-text-muted"
        >
          <tr class="border-b border-lais-border-soft">
            <th colspan="2" class="px-4 py-3 text-left">
              {{
                t('POST_SALE.MANAGEMENT.QUEUE_SALES_GROUP', { count: total })
              }}
            </th>
            <th colspan="4" class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.QUEUE_PAYMENT_GROUP') }}
            </th>
            <th colspan="3" class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.QUEUE_STATUS_GROUP') }}
            </th>
            <th colspan="2" class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.QUEUE_MANAGEMENT_GROUP') }}
            </th>
          </tr>
          <tr>
            <th class="px-4 py-3 text-left">
              {{ t('POST_SALE.MANAGEMENT.QUEUE_COLUMNS.RESPONSIBLE') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.QUEUE_COLUMNS.TOTAL') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.CASH') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.FINANCING') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.FGTS') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.CONSORTIUM') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.ON_TIME') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.ALERT') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.OVERDUE') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.CASH') }}
            </th>
            <th class="px-4 py-3 text-center">
              {{ t('POST_SALE.MANAGEMENT.CHART.FINANCING') }}
            </th>
          </tr>
        </thead>
        <tbody
          class="divide-y divide-lais-border-soft bg-white text-lais-text-body"
        >
          <tr v-if="loading">
            <td
              colspan="11"
              class="px-4 py-12 text-center text-lais-text-muted"
            >
              {{ t('POST_SALE.COMMON.LOADING') }}
            </td>
          </tr>
          <template v-else>
            <PostSaleManagementQueueRow
              v-for="row in rows"
              :key="row.id"
              :row="row"
              :saving="savingUserId === row.id"
              @toggle-queue="emit('toggleQueue', $event)"
            />
          </template>
          <tr v-if="!loading && !rows.length">
            <td
              colspan="11"
              class="px-4 py-12 text-center text-lais-text-muted"
            >
              {{ t('POST_SALE.EMPTY.QUEUE') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </LaisCard>
</template>
